/** Helpers for Offers pipeline — CRM `crm.lead` + `crm.stage` (no name/probability heuristics for columns). */

export type CrmStageRow = {
  id: number;
  name: string;
  display_name?: string;
  sequence: number;
  probability?: number;
  fold?: boolean;
  requirements?: string | false;
};

/** Odoo often returns `false` for empty char fields — treat as missing text. */
export function odooText(v: unknown): string {
  if (v === false || v == null) return "";
  return String(v).trim();
}

export function stageTitle(s: Pick<CrmStageRow, "display_name" | "name" | "id">): string {
  const t = odooText(s.display_name) || odooText(s.name);
  return t || `Stage ${s.id}`;
}

export function assigneeCodeFromUserName(name: string | false | undefined): string {
  const s = String(name || "").trim();
  if (!s) return "??";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
  }
  return s.slice(0, 2).toUpperCase();
}

export function odooDatetimeToDateInput(iso: string | false | undefined): string {
  if (!iso || typeof iso !== "string") return "";
  return iso.slice(0, 10);
}

const KANBAN_PALETTE = [
  { iconColor: "bg-blue-500", countClass: "text-blue-500" },
  { iconColor: "bg-amber-500", countClass: "text-amber-500" },
  { iconColor: "bg-violet-500", countClass: "text-violet-500" },
  { iconColor: "bg-emerald-500", countClass: "text-emerald-500" },
  { iconColor: "bg-red-500", countClass: "text-red-500" },
  { iconColor: "bg-sky-500", countClass: "text-sky-500" },
  { iconColor: "bg-orange-500", countClass: "text-orange-500" },
  { iconColor: "bg-teal-500", countClass: "text-teal-500" },
];

const STAGE_ICONS = ["↗", "⏳", "↔", "✓", "✕", "📋", "◆", "◇", "★", "●"];

export function kanbanVisualForIndex(index: number): {
  icon: string;
  iconColor: string;
  countClass: string;
} {
  const p = KANBAN_PALETTE[index % KANBAN_PALETTE.length];
  return {
    icon: STAGE_ICONS[index % STAGE_ICONS.length],
    iconColor: p.iconColor,
    countClass: p.countClass,
  };
}

/** True when stage is treated as “won” for dashboard stats (Opera / Odoo: high probability on won column). */
export function stageLooksWon(s: Pick<CrmStageRow, "probability"> | null | undefined): boolean {
  return (s?.probability ?? 0) >= 100;
}

/** Opera Offers kanban `crm.stage` ids (see `Offers.tsx` pipeline). */
const OPERA_OFFERS_STAGE_ID_ACCEPTED = 4;
const OPERA_OFFERS_STAGE_ID_DECLINED = 9;

/**
 * True when an opportunity sits in a closed outcome (Accepted / Declined or Odoo won / lost-like).
 * In that case the app creates a new opportunity instead of reusing this lead.
 */
export function opportunityStageIsTerminalAcceptedOrDeclined(
  stageId: number,
  stageLabel: string,
  leadProbability?: number,
): boolean {
  if (stageLooksWon({ probability: leadProbability })) return true;
  if (stageId === OPERA_OFFERS_STAGE_ID_ACCEPTED) return true;
  if (stageId === OPERA_OFFERS_STAGE_ID_DECLINED) return true;
  const t = stageLabel.trim().toLowerCase();
  if (
    t === "accepted" ||
    t === "accepté" ||
    t === "acceptée" ||
    t === "won" ||
    t === "gagné" ||
    t === "gagnee"
  ) {
    return true;
  }
  if (/declin|declined|lost|perdu|refus|reject|refused/i.test(stageLabel)) {
    return true;
  }
  return false;
}
